/**
 * MinionCard — single source of truth for minion card DOM.
 *
 * Historically the card markup was duplicated in three places inside main.ts:
 *   - createHandCardElement          (hand cards)
 *   - createBattlefieldMinionElement (battlefield minions, preparation)
 *   - createCombatMinionElement      (combat board minions)
 *
 * That made it painful to restyle a card: every visual tweak had to be repeated
 * in all three template literals. This module centralizes the card layout so the
 * structure and its styling hooks (CSS class names) are defined exactly ONCE.
 * The three call sites now just describe which variant they need (hand /
 * battlefield / combat) plus any variant-specific extras (actions, draggability,
 * combat damage/dead state).
 *
 * CSS class contract (do NOT rename — relied on by DragAndDropService,
 * CombatMinionDom, showDamageFlash in main.ts, and the test suite):
 *   - wrappers: .hand-card / .battlefield-minion / .combat-minion (+ data-instance-id)
 *   - body:     .minion-art .minion-type-badge .minion-level .minion-name .minion-stats
 *               .minion-stat-card .minion-stat-card-atk .minion-stat-card-hp
 *               .minion-atk .minion-hp .minion-abilities .minion-ability-desc
 *   - combat:   .minion-hp.hp-damaged, wrapper .dead
 *   - actions:  .btn-play (hand); .minion-actions .btn-sell .btn-upgrade (battlefield)
 *               (the action buttons are hidden by CSS — gestures replaced them —
 *               the battlefield additionally shows .minion-upgrade-indicator,
 *               a top-left gold-affordability hint: .can / .cant, absent at max level)
 *
 * Stat layout: each stat lives in its own "mini card" badge (.minion-stat-card)
 * pinned to a bottom corner of the card, overlapping the corner and extending
 * past the card edge — ATK bottom-left, HP bottom-right. The .minion-stats
 * wrapper is display:contents (no box) but is kept in the DOM as the stable
 * query target services/tests select .minion-atk / .minion-hp through.
 *
 * Note: the dynamic type-badge background colour is the only inline style kept —
 * it is data-driven (per minion type) and cannot be expressed as a static class.
 * All static layout/typography lives in the CSS so styling a card is done in one
 * place (see the "Minion Card" section of index.html).
 */

import { MINION_TYPE_NAMES, MINION_TYPE_COLORS, MINION_TYPE_ICONS, MinionType } from '../../shared/models/MinionType';
import { getSpellDefinition, SPELL_MINION_TYPE, UPGRADED_SPELL_ID, ENRICHMENT_SPELL_ID, ENRICHMENT_BASE_GOLD } from '../../shared/models/SpellDefinitions';
import { UPGRADED_STAT_BONUS, UPGRADED_SPELL_STEP_BONUS } from '../../shared/models/AbilityConstants';
import { GameConfig } from '../../config/gameConfig';

/** Which visual/behavioural variant of the card to render. */
export type MinionCardVariant = 'hand' | 'battlefield' | 'combat';

/**
 * The data a minion card needs to render its body.
 *
 * This is the common subset of HandCardRenderData / BattlefieldMinionRenderData /
 * CombatMinionRenderData, so any of those render-data objects can be passed
 * directly (they are structural supersets of this interface).
 */
export interface MinionCardData {
  /** Unique instance ID for this minion copy (becomes data-instance-id). */
  instanceId: string;
  /** The base minion definition ID (displayed as the card name). */
  baseMinionId: string;
  /** The minion's type classification (e.g., "warrior", "mage"). */
  minionType: string;
  /** Current HP of the minion. */
  currentHp: number;
  /** Attack value of the minion. */
  attack: number;
  /** Number of upgrades applied (0 = base). */
  upgradeCount: number;
  /** Computed level: upgradeCount + 1. */
  level: number;
  /** Human-readable ability names (matched by index to abilityDescriptions). */
  abilityNames: string[];
  /** Short descriptions of each ability. */
  abilityDescriptions: string[];
  /** Whether the minion has any abilities. */
  hasAbilities: boolean;
  /**
   * ENRICHMENT (spell, task 197): the card's per-turn counter (how many
   * turns it has sat in the hand). The card's DYNAMIC description shows
   * 1 + this value (the gold the cast pays). Absent on non-Enrichment
   * cards.
   */
  enrichmentTurns?: number;
  /**
   * UPGRADED (spell, task 196): the OWNER's Upgraded cast count — the
   * card's DYNAMIC description shows the buff the NEXT cast would give
   * (+8/+8 + 4×this). Absent on non-Upgraded cards.
   */
  ownerUpgradedCasts?: number;
}

export interface MinionCardOptions {
  /** Which variant to render. */
  variant: MinionCardVariant;
  /**
   * Art image override: the resolved skin image for the OWNING player's
   * selection of this class (see shared/skins resolveSkinImage). When
   * omitted (or undefined) the class's default art (MINION_TYPE_ART) is
   * used — the historical behavior.
   */
  artSrc?: string;
  /** Whether the card element is HTML5-draggable. */
  draggable?: boolean;
  /** Combat only: original HP at combat start (drives the "damaged" HP styling). */
  startingHp?: number;
  /** Combat only: whether the minion is alive (adds the .dead class when false). */
  isAlive?: boolean;
  /** Battlefield only: the player's gold, used for upgrade affordability. */
  playerGold?: number;
  /**
   * Battlefield only: the effective max upgrade count for the card's owner —
   * the base cap (GameConfig.maxUpgrades) EXTENDED by one per Grandmaster
   * minion on the owner's battlefield (the Grandmaster rule; display-only
   * mirror of the authoritative PlayerState.getMaxUpgrades). When omitted,
   * the base cap applies.
   */
  maxUpgrades?: number;
  /** Hand only: invoked when the Play button is clicked. */
  onPlay?: (instanceId: string) => void;
  /** Battlefield only: invoked when the Sell button is clicked. */
  onSell?: (instanceId: string) => void;
  /** Battlefield only: invoked when the Upgrade button is clicked. */
  onUpgrade?: (instanceId: string) => void;
}

/** Wrapper CSS class for each variant (stable contract — see header note). */
const WRAPPER_CLASS: Record<MinionCardVariant, string> = {
  hand: 'hand-card',
  battlefield: 'battlefield-minion',
  combat: 'combat-minion',
};

/**
 * Class card art (full-bleed portrait behind the card chrome), one image per
 * minion type. The files live in public/card-art/ — Vite serves that folder at
 * /card-art/ in dev and copies it into dist/ on build (which the dev server
 * hosts on :3001). Types without an entry (e.g. 'unknown' fallback cards) get
 * no art and keep the plain card background.
 */
const MINION_TYPE_ART: Partial<Record<MinionType, string>> = {
  warrior: '/card-art/Warrior.png',
  mage: '/card-art/Mage.png',
  healer: '/card-art/Healer.png',
  tank: '/card-art/Tank.png',
  bard: '/card-art/Bard.png',
  undead: '/card-art/Undead.png',
  blightcaster: '/card-art/Blightcaster.png',
  buffer: '/card-art/Buffer.png',
  runekeeper: '/card-art/Runekeeper.png',
  spellmaker: '/card-art/SpellMaker.png',
  // Task 213: the Swarmer's art (it appears on the Class Progression
  // grid's level-0 column — the entry was missing, the card rendered
  // without an image).
  swarmer: '/card-art/Swarmer.png',
};

/**
 * PER-CARD art (keyed by baseMinionId) — used by the ITEM class: every
 * item has its OWN fixed image (the user cannot change an item's skin and
 * items appear in no skin menu, so there is no per-class skin resolution
 * for them). Checked before the class-default art; a class with a
 * per-card entry ignores MINION_TYPE_ART for that card.
 */
const CARD_ART_BY_ID: Partial<Record<string, string>> = {
  treasure_chest: '/card-art/TreasureChest.png',
  life_chest: '/card-art/LifeChest.png',
  exploding_barrel: '/card-art/ExplodingBarell.png',
};

/**
 * Shrinks a spell description's font size until its text FITS the card's
 * description box (`.minion-spell-desc` — the middle 80% of the card):
 * starting from `baseSize`, the size drops in 0.5px steps while the rendered
 * text is taller than the box, floored at `minSize` (an absurdly long text
 * at the floor is still clipped by the box's overflow — it can never spill
 * off the card). Measurement is on the real element (scrollHeight vs
 * clientHeight), so it adapts to every card size (hand / battlefield /
 * combat). In environments without layout (jsdom — heights are 0) the loop
 * never runs and the base size is returned.
 *
 * Returns the size that was applied (tests pin the shrinking).
 */
export function fitSpellDescriptionFontSize(
  el: { style: { fontSize: string }; scrollHeight: number; clientHeight: number },
  baseSize: number,
  minSize = 5,
): number {
  let size = baseSize;
  el.style.fontSize = `${size}px`;
  while (el.scrollHeight > el.clientHeight && size > minSize) {
    size = Math.max(minSize, size - 0.5);
    el.style.fontSize = `${size}px`;
  }
  return size;
}

/**
 * SPELL card art — the default background is keyed on the spell's LEVEL
 * (all level-N spells share their level's art: the spell's "skin" is its
 * tier, there is no per-spell image): 1 → Spellcard.png, 2 → Spellcard_1.png,
 * 3 → Spellcard_2.png. The files live at the public/ root (like EyeBtn.png)
 * — Vite serves /Spellcard.png in dev and copies it into dist/ on build.
 */
const SPELL_ART_BY_LEVEL: Partial<Record<number, string>> = {
  1: '/Spellcard.png',
  2: '/Spellcard_1.png',
  3: '/Spellcard_2.png',
};

/**
 * Build a minion card DOM element from a single, shared layout.
 *
 * The common body (type badge, level, name, stats, abilities) is produced here
 * exactly once; only the wrapper class, draggability, damage/dead state and the
 * variant-specific action buttons differ per variant.
 */
export function createMinionCard(data: MinionCardData, options: MinionCardOptions): HTMLElement {
  const {
    variant,
    artSrc,
    draggable = false,
    startingHp,
    isAlive = true,
    playerGold = 0,
    maxUpgrades = GameConfig.maxUpgrades,
    onPlay,
    onSell,
    onUpgrade,
  } = options;

  const el = document.createElement('div');
  el.className = WRAPPER_CLASS[variant];
  el.dataset.instanceId = data.instanceId;
  el.draggable = draggable;

  // Combat only: reflect alive/dead state.
  if (variant === 'combat' && !isAlive) {
    el.classList.add('dead');
  }

  // Type badge (dynamic background colour is the only inline style kept).
  const typeKey = data.minionType as MinionType;
  const typeName = MINION_TYPE_NAMES[typeKey] ?? data.minionType;
  const typeColor = MINION_TYPE_COLORS[typeKey] ?? '#888';
  const typeIcon = MINION_TYPE_ICONS[typeKey] ?? '❓';

  // Class card art: the owning player's selected skin image (artSrc option,
  // resolved upstream via shared resolveSkinImage), then the PER-CARD art
  // (items — each has its own fixed image), then the class default.
  // SPELLS have no per-card art — their background is the LEVEL art
  // (Spellcard.png / Spellcard_1.png / Spellcard_2.png). Omitted when the
  // type has no image at all (e.g. 'unknown' fallback).
  const spellArt = data.minionType === SPELL_MINION_TYPE
    ? SPELL_ART_BY_LEVEL[data.level]
    : undefined;
  const artImage = artSrc ?? CARD_ART_BY_ID[data.baseMinionId] ?? (spellArt ?? MINION_TYPE_ART[typeKey]);
  const artHtml = artImage
    ? `<img class="minion-art" src="${artImage}" alt="" draggable="false">`
    : '';
  // SPELL base color (task 222): a FAINT level tone over the level art —
  // level 1 baby blue, level 2 yellow, level 3 purple (CSS in index.html;
  // the tint spans the card and is pointer-transparent). Spells only —
  // minion cards keep their plain art. (data.level for a spell IS the
  // spec level — the fixed attribute, same key the level art uses.)
  const spellTintHtml = data.minionType === SPELL_MINION_TYPE
    ? `<span class="minion-spell-tint minion-spell-tint-${data.level}" aria-hidden="true"></span>`
    : '';

  // HP shows as "damaged" (red) once it has dropped below the starting value.
  const hpDamaged = variant === 'combat' && startingHp !== undefined && data.currentHp < startingHp;
  const hpClass = hpDamaged ? 'minion-hp hp-damaged' : 'minion-hp';

  // SPELLS (the 'spell' class): the card looks like a minion card but carries
  // NO attack and NO health — the ATK/HP corner badges are replaced by the
  // spell's effect text (shared SpellDefinitions), centered on the card.
  // The statlessness is keyed on the CLASS (an unknown spell id still gets a
  // statless card — it never shows 0/0 badges); the description text only
  // renders when the spec is found.
  const spellDef = data.minionType === SPELL_MINION_TYPE
    ? getSpellDefinition(data.baseMinionId)
    : null;
  const isSpell = data.minionType === SPELL_MINION_TYPE;
  const statsHtml = isSpell
    ? ''
    : `<div class="minion-stats">
      <span class="minion-stat-card minion-stat-card-atk"><span class="minion-atk">⚔${data.attack}</span></span>
      <span class="minion-stat-card minion-stat-card-hp"><span class="${hpClass}">❤${data.currentHp}</span></span>
    </div>`;
  const topLevelHtml = isSpell
    ? ''
    : `<span class="minion-level">Lv.${data.level}</span>`;
  // SPELL TARGET TYPE: centered at the BOTTOM of the card ("Minion" /
  // "Battlefield" from the spec's target field) — tells the player what to
  // aim at before the drag.
  const spellTargetHtml = spellDef
    ? `<span class="minion-spell-target">${spellDef.target === 'minion' ? 'Minion' : 'Battlefield'}</span>`
    : '';
  // (The old .minion-spell-level bottom-center badge is gone — the target
  //  type takes that spot; the spec level stays in the inspect overlay.)
  // SPELL SELL VALUE: bottom-right corner badge (the same "mini card" shape
  // as the minions' HP badge — spells have no HP, the sell value lives in
  // that corner instead).
  const spellSellHtml = spellDef
    ? `<span class="minion-stat-card minion-spell-sell">💰${spellDef.sellValue}g</span>`
    : '';

  // Abilities block (omitted entirely when the minion has no abilities).
  // The keyword font shrinks as the count grows (kw-2 / kw-3 / kw-4 tiers —
  // CSS in the "Minion Card" section of index.html) so the boxed keywords
  // anchored at the card's bottom can never grow taller than the card.
  const keywordCount = Math.max(1, data.abilityNames.length);
  const keywordTierClass =
    keywordCount >= 4 ? ' kw-4' : keywordCount === 3 ? ' kw-3' : keywordCount === 2 ? ' kw-2' : '';
  const abilityHtml = data.hasAbilities
    ? `<span class="minion-abilities${keywordTierClass}">★ ${data.abilityNames.join(', ')}</span>` +
      (data.abilityDescriptions.length > 0
        ? `<span class="minion-ability-desc">${data.abilityDescriptions.join('; ')}</span>`
        : '')
    : '';

  // Variant-specific action area.
  let actionsHtml = '';
  if (variant === 'hand') {
    actionsHtml = `<button class="btn-play" title="Play this minion">Play</button>`;
  } else if (variant === 'battlefield') {
    // The cap is the base max EXTENDED by one per Grandmaster on the owner's
    // battlefield (Grandmaster rule — caller passes the mirror of
    // PlayerState.getMaxUpgrades via the maxUpgrades option); a hardcoded
    // base cap hid the indicator on max-level minions while a Grandmaster
    // was on the board.
    const canUpgrade = data.upgradeCount < maxUpgrades;
    const upgradeCost = GameConfig.upgradeCost + data.upgradeCount;
    const canAffordUpgrade = canUpgrade && playerGold >= upgradeCost;
    actionsHtml = `
      <div class="minion-actions">
        <button class="btn-sell" title="Sell for ${data.level} gold">Sell ${data.level}g</button>
        ${canUpgrade ? `<button class="btn-upgrade" title="Upgrade minion" ${canAffordUpgrade ? '' : 'disabled'}>Upgrade</button>` : ''}
      </div>
    `;
    // The Sell/Upgrade buttons are hidden (gestures replaced them), so the
    // card carries a small top-left indicator showing upgrade affordability:
    //   .can  → player can pay the upgrade cost (solid gold)
    //   .cant → not enough gold yet (dimmed)
    //   (absent when the minion is at its effective max level — nothing to
    //    upgrade to; max = base cap + one per Grandmaster on the board)
    if (canUpgrade) {
      const indicatorClass = canAffordUpgrade ? 'can' : 'cant';
      const indicatorTitle = canAffordUpgrade
        ? `Upgrade for ${upgradeCost}g`
        : `Upgrade costs ${upgradeCost}g — not enough gold`;
      actionsHtml += `\n    <span class="minion-upgrade-indicator ${indicatorClass}" title="${indicatorTitle}">⬆</span>`;
    }
  }

  // SPELLS show their SPEC NAME ("Power touch", "Healing Rain", ...) —
  // rendered under the class badge (.minion-name-spell makes the normally
  // hidden .minion-name visible for spells only).
  const nameValue = spellDef ? spellDef.name : data.baseMinionId;
  const nameClass = spellDef ? 'minion-name minion-name-spell' : 'minion-name';

  // CLASS BADGE: spells read "Spell:<name>" (task 197 — the badge carries
  // the spell's name, not just the class).
  const badgeText = spellDef ? `Spell: ${spellDef.name}` : `${typeIcon} ${typeName}`;

  // DYNAMIC SPELL DESCRIPTIONS (tasks 196/197): the card shows the value
  // IT WOULD give on the next cast — Upgraded: the owner's escalation
  // rung (+8/+8 the first cast, +12/+12 the second, …); Enrichment: the
  // card's accrued gold (1 + turns in hand, per card).
  let spellDescription = spellDef ? spellDef.description : '';
  if (spellDef) {
    if (spellDef.id === UPGRADED_SPELL_ID) {
      const n = UPGRADED_STAT_BONUS + UPGRADED_SPELL_STEP_BONUS * (data.ownerUpgradedCasts ?? 0);
      spellDescription = `Give +${n} HP and +${n} attack. improved by +4 HP and +4 attack when played`;
    } else if (spellDef.id === ENRICHMENT_SPELL_ID) {
      const n = ENRICHMENT_BASE_GOLD + (data.enrichmentTurns ?? 0);
      spellDescription = `Gives ${n} gold when played improve by 1 gold for every turn it stays in the hand.`;
    }
  }

  const spellDescHtml = spellDef
    ? `<span class="minion-spell-desc">${spellDescription}</span>`
    : '';

  el.innerHTML = `
    ${artHtml}
    ${spellTintHtml}
    <span class="minion-type-badge" style="background:${typeColor}">${badgeText}</span>
    ${topLevelHtml}
    <span class="${nameClass}">${nameValue}</span>
    ${statsHtml}
    ${spellDescHtml}
    ${spellTargetHtml}
    ${spellSellHtml}
    ${abilityHtml}
    ${actionsHtml}
  `;

  // SPELLS: shrink the description font until the text fits the card's
  // middle-80% box (10% from the top/bottom edges — the text sits in the
  // middle, centered). The base size matches the CSS variant default
  // (combat cards are smaller → 7px start).
  if (spellDef) {
    const descEl = el.querySelector('.minion-spell-desc') as HTMLElement | null;
    if (descEl) {
      fitSpellDescriptionFontSize(descEl, variant === 'combat' ? 7 : 10);
    }
  }

  // Wire the variant-specific action buttons (each only exists for its variant).
  if (variant === 'hand') {
    const playBtn = el.querySelector('.btn-play') as HTMLButtonElement;
    playBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onPlay?.(data.instanceId);
    });
  } else if (variant === 'battlefield') {
    const sellBtn = el.querySelector('.btn-sell') as HTMLButtonElement;
    sellBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onSell?.(data.instanceId);
    });
    const upgradeBtn = el.querySelector('.btn-upgrade');
    if (upgradeBtn) {
      upgradeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onUpgrade?.(data.instanceId);
      });
    }
  }

  return el;
}
